import { getUserByIds } from '@/lib/zybooks-api';
import authService from '@/services/zybook-auth-service';
import { defaultMapDiffToDecorations, type ChangeTrackingTransform, type Diff } from '@tiptap-pro/extension-snapshot-compare';
import type { Editor } from '@tiptap/core';
import type { Decoration } from '@tiptap/pm/view';
import type { CSSProperties } from 'react';

declare module '@tiptap-pro/extension-snapshot-compare' {
    interface Attribution {
        userName?: string;
    }
}

const LOOKUP_CHUNK_SIZE = 50;
const UNKNOWN_USER_COLOR = '#e5e7eb';
const INSERT_TEXT_COLOR = '#000';
const DELETE_TEXT_COLOR = '#777';
const INSERT_DIFF_COLOR = '#22c55e';
const DELETE_DIFF_COLOR = '#ef4444';
const INSERT_ALPHA = 'cc';
const DELETE_ALPHA = '99';

export type SnapshotCompareDiffColorOptions = {
    colorByUserId?: boolean;
};

const DEFAULT_SNAPSHOT_COMPARE_DIFF_COLOR_OPTIONS: Required<SnapshotCompareDiffColorOptions> = {
    colorByUserId: false,
};

export type SnapshotCompareLegendUser = {
    userId: string | undefined;
    displayName: string;
};

export type ForwardedSnapshotCompareDecorationAttrs = {
    className?: string;
    style?: CSSProperties;
    'data-diff-type'?: string;
    'data-diff-user-id'?: string;
    'data-diff-user-name'?: string;
    'data-diff-diff-content-type'?: string;
};

const displayNameByUserId = new Map<string, string>();
const unknownUserIds = new Set<string>();
let inFlightEnsure: Promise<void> | null = null;
const pendingLookupUserIds = new Set<string>();
const cachedUserColorsById = new Map<string, string>();

export function resolveSnapshotCompareDiffColorOptions(
    options?: SnapshotCompareDiffColorOptions,
): Required<SnapshotCompareDiffColorOptions> {
    return {
        colorByUserId: options?.colorByUserId ?? DEFAULT_SNAPSHOT_COMPARE_DIFF_COLOR_OPTIONS.colorByUserId,
    };
}

export function applySnapshotCompareDiffColorOptions(editor: Editor, options: SnapshotCompareDiffColorOptions): void {
    const storage = editor.storage.snapshotCompare as { diffColorOptions?: SnapshotCompareDiffColorOptions } | undefined;
    if (!storage) {
        return;
    }

    storage.diffColorOptions = resolveSnapshotCompareDiffColorOptions(options);
}

function getSnapshotCompareDiffColorOptions(editor?: Editor): Required<SnapshotCompareDiffColorOptions> {
    if (editor) {
        const storage = editor.storage.snapshotCompare as { diffColorOptions?: SnapshotCompareDiffColorOptions } | undefined;
        if (storage?.diffColorOptions) {
            return resolveSnapshotCompareDiffColorOptions(storage.diffColorOptions);
        }
    }

    return resolveSnapshotCompareDiffColorOptions();
}

export function seedSnapshotUserFromAuth(): void {
    const userId = authService.getUserId();
    const fullName = authService.getUserFullName()?.trim();
    if (userId && fullName) {
        displayNameByUserId.set(userId, fullName);
    }
}

export function getCachedUserDisplayName(userId: string): string | undefined {
    return displayNameByUserId.get(userId);
}

export function resolveSnapshotCompareUserDisplayName(userId: string | undefined): string {
    if (!userId) {
        return 'Unknown';
    }

    const cached = displayNameByUserId.get(userId);
    if (cached) {
        return cached;
    }

    if (userId === authService.getUserId()) {
        return authService.getUserFullName()?.trim() ?? userId;
    }

    if (!/^\d+$/.test(userId)) {
        return userId;
    }

    return userId;
}

export function collectUserIdsFromDiffSet(diffs: Array<{ attribution: { userId?: string | null } }>): string[] {
    const userIds = new Set<string>();

    for (const diff of diffs) {
        const userId = diff.attribution.userId;
        if (userId && /^\d+$/.test(userId)) {
            userIds.add(userId);
        }
    }

    return [...userIds];
}

export async function ensureUserDisplayNames(userIds: string[]): Promise<void> {
    const missing = getMissingLookupUserIds(userIds);
    if (missing.length === 0) {
        return;
    }

    for (const userId of missing) {
        pendingLookupUserIds.add(userId);
    }

    if (!inFlightEnsure) {
        inFlightEnsure = flushPendingLookups().finally(() => {
            inFlightEnsure = null;
        });
    }

    await inFlightEnsure;
}

export function enrichDiffAttributions<T extends { attribution: { userId?: string | null; userName?: string } }>(diffs: T[]): T[] {
    return diffs.map(diff => {
        const userId = diff.attribution.userId ?? undefined;
        const userName = resolveSnapshotCompareUserDisplayName(userId);

        if (diff.attribution.userName === userName) {
            return diff;
        }

        return {
            ...diff,
            attribution: {
                ...diff.attribution,
                userName,
            },
        };
    });
}

/** @internal Test helper */
export function resetSnapshotUserDirectoryForTests(): void {
    displayNameByUserId.clear();
    unknownUserIds.clear();
    pendingLookupUserIds.clear();
    inFlightEnsure = null;
    cachedUserColorsById.clear();
}

export function getSnapshotCompareUserColor(userId: string | undefined): string {
    if (!userId) {
        return UNKNOWN_USER_COLOR;
    }

    const cached = cachedUserColorsById.get(userId);
    if (cached) {
        return cached;
    }

    const color = hashStringToHexColor(userId);
    cachedUserColorsById.set(userId, color);
    return color;
}

export function getSnapshotCompareDiffColors(userId: string | undefined, options?: SnapshotCompareDiffColorOptions) {
    const { colorByUserId } = resolveSnapshotCompareDiffColorOptions(options);

    if (colorByUserId) {
        const baseColor = getSnapshotCompareUserColor(userId);

        return {
            insert: {
                color: INSERT_TEXT_COLOR,
                backgroundColor: withHexAlpha(baseColor, INSERT_ALPHA),
            },
            delete: {
                color: DELETE_TEXT_COLOR,
                backgroundColor: withHexAlpha(baseColor, DELETE_ALPHA),
            },
        };
    }

    return {
        insert: {
            color: INSERT_TEXT_COLOR,
            backgroundColor: withHexAlpha(INSERT_DIFF_COLOR, INSERT_ALPHA),
        },
        delete: {
            color: DELETE_TEXT_COLOR,
            backgroundColor: withHexAlpha(DELETE_DIFF_COLOR, DELETE_ALPHA),
        },
    };
}

export function buildAttributionColor(userId: string | undefined, options?: SnapshotCompareDiffColorOptions) {
    const colors = getSnapshotCompareDiffColors(userId, options);

    return {
        color: colors.insert.color,
        backgroundColor: colors.insert.backgroundColor,
        insert: colors.insert,
        delete: colors.delete,
    };
}

export function getSnapshotCompareUserColorWithAlpha(
    userId: string | undefined,
    alpha = INSERT_ALPHA,
    options?: SnapshotCompareDiffColorOptions,
): string {
    const { colorByUserId } = resolveSnapshotCompareDiffColorOptions(options);
    const baseColor = colorByUserId ? getSnapshotCompareUserColor(userId) : INSERT_DIFF_COLOR;
    return withHexAlpha(baseColor, alpha);
}

export function forwardSnapshotCompareDecorationAttrs(
    decorations: readonly Decoration[] | undefined,
): ForwardedSnapshotCompareDecorationAttrs {
    if (!decorations?.length) {
        return {};
    }

    const classes: string[] = [];
    let style: CSSProperties = {};
    const result: ForwardedSnapshotCompareDecorationAttrs = {};

    for (const decoration of decorations) {
        const attrs = getDecorationAttrs(decoration);
        if (attrs) {
            for (const [key, value] of Object.entries(attrs)) {
                if (value == null) {
                    continue;
                }

                const stringValue = String(value);

                if (key === 'class') {
                    classes.push(stringValue);
                } else if (key === 'style') {
                    style = { ...style, ...parseInlineStyle(stringValue) };
                } else if (key === 'data-diff-type') {
                    result['data-diff-type'] = stringValue;
                } else if (key === 'data-diff-user-id') {
                    result['data-diff-user-id'] = stringValue;
                } else if (key === 'data-diff-user-name') {
                    result['data-diff-user-name'] = stringValue;
                } else if (key === 'data-diff-diff-content-type') {
                    result['data-diff-diff-content-type'] = stringValue;
                }
            }
        }

        const diff = getDiffFromDecorationSpec(decoration);
        if (diff) {
            applyDiffToForwardedAttrs(diff, result, style);
        }
    }

    if (classes.length > 0) {
        result.className = classes.join(' ');
    }

    if (Object.keys(style).length > 0) {
        result.style = style;
    }

    return result;
}

export function mapSnapshotCompareDiffToDecorations({
    diff,
    tr,
    editor,
    defaultMapDiffToDecorations: mapDiff,
}: {
    diff: Diff;
    tr: ChangeTrackingTransform;
    editor: Editor;
    defaultMapDiffToDecorations: typeof defaultMapDiffToDecorations;
}) {
    const colorOptions = getSnapshotCompareDiffColorOptions(editor);
    const userName = diff.attribution.userName?.trim() || resolveSnapshotCompareUserDisplayName(diff.attribution.userId);

    const enrichedDiff = {
        ...diff,
        attribution: {
            ...diff.attribution,
            userName,
            color: buildAttributionColor(diff.attribution.userId, colorOptions),
        },
    };

    const attributes: Record<string, any> = {
        'data-diff-user-name': userName,
        'data-diff-diff-content-type': diff.diffContentType,
    };

    const blockDiffStyle = buildBlockDiffDecorationStyle(enrichedDiff, colorOptions);
    if (blockDiffStyle) {
        attributes.style = blockDiffStyle;
    }

    return mapDiff({
        diff: enrichedDiff,
        tr,
        editor,
        attributes,
    });
}

export function collectSnapshotCompareLegendUsers(
    diffs: Array<{ attribution: { userId?: string | null; userName?: string } }>,
): SnapshotCompareLegendUser[] {
    const usersByKey = new Map<string, SnapshotCompareLegendUser>();

    for (const diff of diffs) {
        const userId = diff.attribution.userId ?? undefined;
        const key = userId ?? '__unknown__';
        const displayName = diff.attribution.userName?.trim() || resolveSnapshotCompareUserDisplayName(userId);

        if (!usersByKey.has(key)) {
            usersByKey.set(key, { userId, displayName });
        }
    }

    return [...usersByKey.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function getMissingLookupUserIds(userIds: string[]): string[] {
    return [...new Set(userIds)].filter(userId => !displayNameByUserId.has(userId) && !unknownUserIds.has(userId));
}

async function flushPendingLookups(): Promise<void> {
    while (pendingLookupUserIds.size > 0) {
        const batch = [...pendingLookupUserIds].slice(0, LOOKUP_CHUNK_SIZE);
        for (const userId of batch) {
            pendingLookupUserIds.delete(userId);
        }

        const results = await getUserByIds(batch);
        for (const [userId, displayName] of results) {
            displayNameByUserId.set(userId, displayName);
        }

        for (const userId of batch) {
            if (!displayNameByUserId.has(userId)) {
                unknownUserIds.add(userId);
            }
        }
    }
}

function hashStringToHexColor(value: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = value.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash |= 0;

    const hue = Math.abs(hash) % 360;
    const saturation = (60 + (Math.abs(hash >> 8) % 25)) / 100;
    const lightness = (74 + (Math.abs(hash >> 16) % 10)) / 100;
    const chroma = saturation * Math.min(lightness, 1 - lightness);
    const hueToRgb = (n: number) => {
        const k = (n + hue / 30) % 12;
        const color = lightness - chroma * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0');
    };

    return `#${hueToRgb(0)}${hueToRgb(8)}${hueToRgb(4)}`;
}

function withHexAlpha(color: string, alpha: string): string {
    if (color.startsWith('#') && color.length === 7) {
        return `${color}${alpha}`;
    }

    return color;
}

function buildBlockDiffDecorationStyle(diff: Diff, options?: SnapshotCompareDiffColorOptions): string | undefined {
    if (diff.type !== 'block-insert' && diff.type !== 'block-delete' && diff.type !== 'block-update') {
        return undefined;
    }
    const colors = getSnapshotCompareDiffColors(diff.attribution.userId ?? undefined, options);
    const channel = diff.type === 'block-delete' ? colors.delete : colors.insert;
    return `background-color: ${channel.backgroundColor}; color: ${channel.color}; padding: 4px;`;
}

function parseInlineStyle(style: string): CSSProperties {
    const result: Record<string, string> = {};

    for (const part of style.split(';')) {
        const separatorIndex = part.indexOf(':');
        if (separatorIndex === -1) {
            continue;
        }

        const key = part.slice(0, separatorIndex).trim();
        const value = part.slice(separatorIndex + 1).trim();
        if (!key || !value) {
            continue;
        }

        const camelKey = key.replace(/-([a-z])/g, (_, character: string) => character.toUpperCase());
        result[camelKey] = value;
    }

    return result;
}

function getDecorationAttrs(decoration: Decoration): Record<string, unknown> | null {
    const type = (decoration as unknown as { type?: { attrs?: Record<string, unknown> } }).type;
    return type?.attrs ?? null;
}

function getDiffFromDecorationSpec(decoration: Decoration): Diff | undefined {
    return (decoration.spec as { diff?: Diff } | undefined)?.diff;
}

function applyDiffToForwardedAttrs(diff: Diff, result: ForwardedSnapshotCompareDecorationAttrs, style: CSSProperties) {
    if (!result['data-diff-type']) {
        result['data-diff-type'] = diff.type;
    }

    if (!result['data-diff-user-id'] && diff.attribution.userId) {
        result['data-diff-user-id'] = diff.attribution.userId;
    }

    if (!result['data-diff-user-name']) {
        result['data-diff-user-name'] = diff.attribution.userName?.trim() || resolveSnapshotCompareUserDisplayName(diff.attribution.userId);
    }

    if (Object.keys(style).length === 0) {
        const blockDiffStyle = buildBlockDiffDecorationStyle(diff);
        if (blockDiffStyle) {
            Object.assign(style, parseInlineStyle(blockDiffStyle));
        }
    }
}
